import axios from 'axios';
import * as Raven from 'raven-js';
import {
  addTranslationForLanguage,
  initialize,
  setActiveLanguage,
} from 'react-localize-redux';
import * as Redux from 'redux';

import { getTranslationOrDefault } from '../../localization';
import * as analytics from '../analytics';
import * as Api from '../Api';
import { cookieConsent } from '../cookieconsent';
import * as Data from '../Data';
import { setCookieConsent } from '../redux/cookie-consent/actions';
import { Routes } from '../Routes';
import { storage } from '../services/storageServices';
import * as Store from '../Store';
import { Utilities } from '../Utilities';
import { AddLocation, SetClientAnalytics, SetCountryCode } from './Actions';

const applyCallbackToMenu = (
  data: Api.RestaurantSummaryCommon[],
  callbackFn?: (vrid: number) => void,
) => {
  if (data.length === 0) {
    return;
  }

  if (callbackFn) {
    const vrid = data[0].VirtualRestaurantId;
    callbackFn(vrid);
  }
};
export const FindInitialRestaurant = (
  then?: (vrid: number) => any,
  _iteration?: number,
) => {
  return (
    dispatch: Redux.Dispatch<Redux.Action>,
    getState: () => Store.State,
  ) => {
    const dispatchThunk = dispatch as any;

    const {
      AppConfig,
      Config,
      Location: { geo },
    } = getState().flipdish;

    const q = {
      count: 1000,
      Latitude: geo.Latitude,
      Longitude: geo.Longitude,
      skip: 0,
    };

    if (Config.IncludePhysicalRestaurantIds) {
      q['includePhysicalRestaurantIds'] = Config.IncludePhysicalRestaurantIds;
    }

    if (AppConfig.status === 'success' && AppConfig.data.PickupEnabled) {
      Utilities.toPromise<Api.CollectionRestaurantSummary[]>(
        axios.get(
          '/Restaurant/PickupPhysicalRestaurantSummariesFromCoordinates',
          { params: q },
        ),
      )
        .then((data) => {
          dispatch({
            type: 'SET_COLLECTION_SUMMARIES',
            payload: {
              status: 'success',
              data,
            },
          });

          data.forEach((r) => {
            dispatchThunk(LoadCollectionDetails(r.PhysicalRestaurantId, false));
          });

          applyCallbackToMenu(data, then);
        })
        .catch((_error) => {
          dispatch({
            type: 'SET_COLLECTION_SUMMARIES',
            payload: {
              status: 'none',
            },
          });
        });
    }

    if (AppConfig.status === 'success' && AppConfig.data.DeliveryEnabled) {
      const deliveryParams = {
        count: 1000,
        deviceId: Config.DeviceId,
        Latitude: geo.Latitude,
        Longitude: geo.Longitude,
        skip: 0,
      };

      Utilities.toPromise<Api.DeliveryRestaurantSummary[]>(
        axios.get('/Restaurant/DeliveryRestaurantSummaries', {
          params: deliveryParams,
        }),
      )
        .then((deliveryData) => {
          dispatch({
            type: 'SET_DELIVERY_SUMMARIES',
            payload: {
              status: 'success',
              data: deliveryData,
            },
          });

          applyCallbackToMenu(deliveryData, then);
        })
        .catch((_error) => {
          dispatch({
            type: 'SET_DELIVERY_SUMMARIES',
            payload: {
              status: 'none',
            },
          });
        });
    }
  };
};

export const LoadAppconfig = () => {
  return (dispatch: Redux.Dispatch<Redux.Action>) => {
    const dispatchThunk = dispatch as any;

    dispatchThunk(LoadGeoIpLookup());

    //Work with the cached version
    const cachedConfigJson = storage.getItem<Api.AppConfig>(
      'flipdish-app-config',
    );

    if (cachedConfigJson) {
      try {
        if (cachedConfigJson.Languages) {
          const languages = cachedConfigJson.Languages.map(
            (lang: Api.Language) => {
              return lang.LangageId;
            },
          );

          //TODO: Check if localization file is not there
          dispatchThunk(initialize(languages, { renderInnerHtml: false }));

          const currentLanguageInStorage = storage.getItem(
            'flipdish-current-language',
          );

          for (const language of languages) {
            const [localeCode, langJsonWithFallback] = getTranslationOrDefault(
              language,
            );

            dispatchThunk(
              addTranslationForLanguage(
                Utilities.translateTransform(langJsonWithFallback),
                localeCode,
              ),
            );

            //If there is a language in the localStorage and it is in the list of allowed langauges set it as active
            if (
              currentLanguageInStorage &&
              localeCode === currentLanguageInStorage
            ) {
              dispatchThunk(setActiveLanguage(localeCode));
            }
          }
        }

        dispatchThunk(
          AddLocation(
            {
              Latitude: cachedConfigJson.MapCenterLatitude,
              Longitude: cachedConfigJson.MapCenterLongitude,
            },
            Store.LocationCertainty.AppConfig,
          ),
        );

        dispatch({
          type: 'LOAD_APP_CONFIG',
          payload: cachedConfigJson,
        });
      } catch (error) {}
    }

    const load = axios.get('/Account/AppConfig');
    load.catch((e) => {
      Raven.captureMessage('Error downloading app config', {
        tags: { category: 'API failure' },
        extra: {
          error: Utilities.handleError(e),
          appnameid: Utilities.extraOption('data-restaurant') || '',
        },
      });
      //alert('There was an error loading this restaurants configuration. Please refresh the page and try again.');
    });

    load.then((data) => {
      let config: Api.AppConfig = data.data.Data;
      console.log('Loaded App config', config);

      // Set legal docs urls
      const CookiePolicyUrl =
        Utilities.extraOption('data-cookie-policy-link') ||
        config.CookiePolicyUrl ||
        'https://www.flipdish.com/cookie-policy/';
      const TermsAndConditionsUrl =
        Utilities.extraOption('data-terms-link') ||
        config.TermsAndConditionsUrl ||
        'https://www.flipdish.com/docs/tac/';
      const PrivacyPolicyUrl =
        Utilities.extraOption('data-privacy-policy-link') ||
        config.PrivacyPolicyUrl ||
        'https://www.flipdish.com/privacy-policy/';

      config = {
        ...config,
        CookiePolicyUrl,
        TermsAndConditionsUrl,
        PrivacyPolicyUrl,
      };

      Utilities.setInLocalStorage('flipdish-app-config', config);

      const languages = data.data.Data.Languages.map((lang: Api.Language) => {
        return lang.LangageId;
      });

      //TODO: Check if localization file is not there
      dispatchThunk(initialize(languages, { renderInnerHtml: false }));

      const currentLanguageInStorage =
        storage.getItem('flipdish-current-language') ||
        Utilities.extraOption('data-language') ||
        Utilities.extraOption('data-initial-language') ||
        getLanguage();
      function getLanguage() {
        if (navigator.languages !== undefined) {
          const [localeCode] = getTranslationOrDefault(navigator.languages[0]);
          return localeCode;
        } else {
          const [localeCode] = getTranslationOrDefault(navigator.language);
          return localeCode;
        }
      }

      let activeLang: string;
      for (const language of languages) {
        const [localeCode, langJsonWithFallback] = getTranslationOrDefault(
          language,
        );

        dispatchThunk(
          addTranslationForLanguage(
            Utilities.translateTransform(langJsonWithFallback),
            localeCode,
          ),
        );

        //If there is a language in the localStorage and it is in the list of allowed langauges set it as active
        if (
          currentLanguageInStorage &&
          localeCode === currentLanguageInStorage
        ) {
          dispatchThunk(setActiveLanguage(localeCode));
          activeLang = localeCode;
        }
      }

      dispatchThunk(
        AddLocation(
          {
            Latitude: config.MapCenterLatitude,
            Longitude: config.MapCenterLongitude,
          },
          Store.LocationCertainty.AppConfig,
        ),
      );

      dispatch({
        type: 'LOAD_APP_CONFIG',
        payload: config,
      });

      const disableNonEssentialCookies = config.Features.includes(
        'disable-non-essential-cookies',
      );
      const fdCcEnabledFF =
        config.ComplianceType === Api.ComplianceType.GdprCompliance ||
        ('ComplianceType' in config === false &&
          config.Features.includes('enable-gdpr-cookies'));

      const setupGA = (disable: boolean) =>
        analytics.initialize(
          config.GoogleAnalyticsTokens.map((trackingId) => [trackingId, {}]),
          disable,
        );

      if (disableNonEssentialCookies) {
        setupGA(disableNonEssentialCookies);
      } else if (fdCcEnabledFF) {
        const restaurantName = config.BrandName;
        const logoUrl = config.BrandLogoUrl;

        // Cookie consent modal
        cookieConsent.init(
          {
            lang: activeLang || 'en',
            logoUrl,
            restaurantName,
            cookiePolicyUrl: config.CookiePolicyUrl || '',
          },
          (selected) => {
            const isPerformanceEnabled = selected.includes('performance');
            const isAdvertisingEnabled = selected.includes('advertising');

            // Facebook
            if (isAdvertisingEnabled && config.FacebookTokens.length > 0) {
              analytics.InitFacebook(config.FacebookTokens[0]);
            }

            // Google Analytics
            setupGA(!isPerformanceEnabled);

            dispatch(
              setCookieConsent(
                selected as WebOrder.State.CookieConsent['selectedGroups'],
              ),
            );
          },
        );
      } else {
        if (config.IntercomAppId) {
          //Intercom.Load(config.IntercomAppId, getState().intercom);
        }

        if (config.FacebookTokens.length > 0) {
          analytics.InitFacebook(config.FacebookTokens[0]);
        }

        setupGA(disableNonEssentialCookies);
      }

      //If client analytics is not present it will still set ClientAnalytics
      //to true so GA sends event's to Flipdish's Google Analytics
      dispatchThunk(SetClientAnalytics(true));
    });
  };
};

export const LoadCollectionDetails = (prid: number, refresh: boolean) => {
  return (
    dispatch: Redux.Dispatch<Redux.Action>,
    getState: () => Store.State,
  ) => {
    const dispatchCollection = (
      r: Data.Async<Api.CollectionRestaurantDetail>,
    ) => {
      dispatch({
        type: 'SET_RESTAURANT_COLLECTION',
        payload: {
          restaurant: r,
          prid,
        },
      });
    };

    const Restaurant = getState().collectionRestaurants[prid];

    //Work with the cached version
    const pickupRestaurantJson = storage.getItem<
      Api.CollectionRestaurantDetail
    >('flipdish-pickup-restaurant-' + prid);
    const restaurantExists = Restaurant && Restaurant.status === 'success';

    /*Get from localStorage if it exists in localStorage, is not in redux store
    and is on an initial pass (hence new data from API is not required)*/
    if (pickupRestaurantJson && !restaurantExists && !refresh) {
      const detail = pickupRestaurantJson;
      detail.FromAPI = false;
      dispatchCollection({ status: 'success', data: detail });
    }

    axios
      .get(`/Restaurant/PickupRestaurantDetails/${prid}`)
      .then((r) => {
        const detail: Api.CollectionRestaurantDetail = r.data.Data;

        Utilities.setInLocalStorage(
          'flipdish-pickup-restaurant-' + prid,
          detail,
        );
        detail.FromAPI = true;

        dispatchCollection({
          status: 'success',
          data: detail,
        });
      })
      .catch((_e) => {
        const appconfig: any = {};

        const flipdishAppConfig = getState().flipdish.AppConfig;

        if (flipdishAppConfig.status === 'success') {
          appconfig.AppNameId = flipdishAppConfig.data.AppNameId;
          appconfig.WhiteLabelId = flipdishAppConfig.data.WhiteLabelId;
        }

        // sentry-disable-quota-overflow
        // Raven.captureMessage('Error loading collection restaurant', {
        //   tags: { category: 'API failure' },
        //   extra: {
        //     error: Utilities.handleError(e),
        //     appconfig: appconfig,
        //     prid: prid
        //   }
        // });

        if (!pickupRestaurantJson) {
          dispatchCollection({ status: 'none' });
        }
      });
  };
};

export const LoadDeliveryDetails = (
  vrid: number,
  deliveryLocationId: number,
  refresh = false,
) => {
  return (
    dispatch: Redux.Dispatch<Redux.Action>,
    getState: () => Store.State,
  ) => {
    const dis = (r: Data.Async<Api.DeliveryRestaurantDetail>) => {
      dispatch({
        type: 'SET_RESTAURANT_DELIVERY',
        payload: {
          restaurant: r,
          vrid,
          dlid: deliveryLocationId,
        },
      });
    };

    const key = Store.DeliveryKey(vrid, deliveryLocationId);
    const Restaurant = getState().deliveryRestaurants[key];

    const restaurantExistsFromApi =
      Restaurant && Restaurant.status === 'success';

    if (restaurantExistsFromApi && !refresh) {
      return;
    }

    // TODO:
    // Assert dl has a valid integer ID.
    // Assert valid device ID
    const query = {
      deviceId: getState().flipdish.Config.DeviceId,
      deliveryLocationId,
    };

    const req = axios.get('/Restaurant/DeliveryRestaurantDetails/' + vrid, {
      params: query,
      retry: 3,
      retryDelay: 1000,
    });

    req.catch((_e) => {
      dis({ status: 'none' });
    });

    req.then((r) => {
      const detail: Api.DeliveryRestaurantDetail = r.data.Data;
      if (detail) {
        detail.FromAPI = true;
        dis({ status: 'success', data: detail });
      }
    });
  };
};

export const LoadGeoIpLookup = () => {
  // Thunk
  return (dispatch: Redux.Dispatch<Redux.Action>) => {
    const isDev = process.env.NODE_ENV === 'development';
    const isOperaMini = navigator.userAgent.indexOf('Opera Mini') > -1;
    if (isDev || isOperaMini) {
      return;
    }

    interface IpInfo {
      ip: string;
      hostname: string;
      city: string;
      region: string;
      country: string;
      loc: string;
      org: string;
    }

    const commonHeaders = { ...axios.defaults.headers.common };

    delete commonHeaders['X-Flipdish-Token'];
    delete commonHeaders['X-Coordinates'];
    delete commonHeaders['Flipdish-App-Type'];
    delete commonHeaders['Flipdish-White-Label-Id'];

    const query = axios.get('/json', {
      baseURL: '//ipinfo.io',
      withCredentials: false,
      headers: {
        common: commonHeaders,
      },
    });

    query.then((data) => {
      const result: IpInfo = data.data;
      const [lat, lng] = result.loc.split(',').map(parseFloat);

      const geo = { Latitude: lat, Longitude: lng };
      dispatch(SetCountryCode(result.country));
      dispatch(AddLocation(geo, Store.LocationCertainty.GeoIp));
    });
    query.catch(() => {
      console.error('Error looking up ip address on ipinfo.io');
    });
  };
};

export const LoadMenuByUrl = (menuUrl: string) => {
  return (
    dispatch: Redux.Dispatch<Redux.Action>,
    getState: () => Store.State,
  ) => {
    let MenuData = getState().flipdish.MenuData;
    const pending: Store.MenuDataPanding = {
      type: 'MENU_DATA_PENDING',
      menuKey: menuUrl,
    };

    let existingMenuData = MenuData[menuUrl];

    //If menu doesn't exist in the redux store dispatch pending and get menu from localStorage if it exists
    if (!existingMenuData) {
      dispatch(pending);

      const menuJson = storage.getItem<Api.MenuReposnse>(menuUrl);

      if (menuJson) {
        menuJson.FromAPI = false;

        const action: Store.AddMenuData = {
          type: 'ADD_MENU_DATA',
          menuKey: menuUrl,
          menu: menuJson,
        };

        dispatch(action);
      }
    }

    //Check for if the menu data in the redux store has been got from the API
    let existingMenuFromAPI = false;

    MenuData = getState().flipdish.MenuData;
    existingMenuData = MenuData[menuUrl];

    if (existingMenuData && existingMenuData.status === 'success') {
      existingMenuFromAPI = existingMenuData.data.FromAPI;
    }

    if (existingMenuFromAPI) {
      return;
    }

    axios({
      method: 'get',
      url: menuUrl,
      withCredentials: false,
    }).then((response) => {
      const menu: any = response.data;
      menu.FromAPI = true;

      menu.MenuSections = Data.Sorted(menu.MenuSections);
      Utilities.setInLocalStorage(menuUrl, menu);

      const action: Store.AddMenuData = {
        type: 'ADD_MENU_DATA',
        menuKey: menuUrl,
        menu,
      };

      dispatch(action);
    });
  };
};

export const LoadRestaurant = (props: Routes) => {
  const { params } = props;

  const vrId = parseInt(`${params.vrid}`);
  const deliveryType = params.type;

  return (
    dispatch: Redux.Dispatch<Redux.Action>,
    getState: () => Store.State,
  ) => {
    const dispatchThunk = dispatch as any;

    const { CollectionSummaries, DeliverySummaries } = getState().flipdish;

    let Summary: Api.RestaurantSummaryCommon;
    switch (deliveryType) {
      case 'collection': {
        const prId = parseInt(`${params.id}`);
        if (CollectionSummaries.status === 'success' && prId) {
          Summary = CollectionSummaries.data.find(
            (s) =>
              s.VirtualRestaurantId === vrId && s.PhysicalRestaurantId === prId,
          );

          dispatchThunk(LoadCollectionDetails(prId, false));
        }
        break;
      }
      case 'delivery': {
        const deliveryLocationId = parseInt(`${params.id}`);
        if (DeliverySummaries.status === 'success' && deliveryLocationId) {
          Summary = DeliverySummaries.data.find(
            (s) => s.VirtualRestaurantId === vrId,
          );

          dispatchThunk(LoadDeliveryDetails(vrId, deliveryLocationId, true));
        }
        break;
      }
      default:
        // why this!!! idk but was like this before
        const getSummary = (
          summaries: Data.Async<Api.RestaurantSummaryCommon[]>,
        ) => {
          return (
            summaries.status === 'success' &&
            summaries.data.find((s) => s.VirtualRestaurantId === vrId)
          );
        };
        if (
          CollectionSummaries.status === 'success' &&
          CollectionSummaries.data.length > 0
        ) {
          Summary = getSummary(CollectionSummaries);
        } else if (DeliverySummaries.status === 'success') {
          Summary = getSummary(DeliverySummaries);
        }
        break;
    }

    //If summary load menu
    if (Summary) {
      Utilities.loadMenu([Summary], dispatch, getState, false);
    }
  };
};

export const getDataForAllRestaurants = () => {
  return (
    dispatch: Redux.Dispatch<Redux.Action>,
    getState: () => Store.State,
  ) => {
    const dispatchThunk = dispatch as any;

    const {
      AppConfig,
      Config,
      Location: { geo },
    } = getState().flipdish;

    if (AppConfig.status === 'success' && AppConfig.data.PickupEnabled) {
      const collectionParams = {
        count: 1000,
        Latitude: geo.Latitude,
        Longitude: geo.Longitude,
        skip: 0,
      };

      if (Config.IncludePhysicalRestaurantIds) {
        collectionParams['includePhysicalRestaurantIds'] =
          Config.IncludePhysicalRestaurantIds;
      }

      Utilities.toPromise<Api.CollectionRestaurantSummary[]>(
        axios.get(
          '/Restaurant/PickupPhysicalRestaurantSummariesFromCoordinates',
          { params: collectionParams },
        ),
      )
        .then((data) => {
          dispatch({
            type: 'SET_COLLECTION_SUMMARIES',
            payload: {
              status: 'success',
              data,
            },
          });

          data.forEach((r) => {
            dispatchThunk(LoadCollectionDetails(r.PhysicalRestaurantId, false));
          });
        })
        .catch((_error) => {
          dispatch({
            type: 'SET_COLLECTION_SUMMARIES',
            payload: {
              status: 'none',
            },
          });
        });
    }

    if (AppConfig.status === 'success' && AppConfig.data.DeliveryEnabled) {
      const deliveryParams = {
        count: 1000,
        deviceId: Config.DeviceId,
        Latitude: geo.Latitude,
        Longitude: geo.Longitude,
        skip: 0,
      };

      Utilities.toPromise<Api.DeliveryRestaurantSummary[]>(
        axios.get('/Restaurant/DeliveryRestaurantSummaries', {
          params: deliveryParams,
        }),
      )
        .then((deliveryData) => {
          dispatch({
            type: 'SET_DELIVERY_SUMMARIES',
            payload: {
              status: 'success',
              data: deliveryData,
            },
          });
        })
        .catch((_error) => {
          dispatch({
            type: 'SET_DELIVERY_SUMMARIES',
            payload: {
              status: 'none',
            },
          });
        });
    }
  };
};

export const getRestaurantData = (
  vrId: number,
  orderType: string,
  id: number,
) => {
  return (
    dispatch: Redux.Dispatch<Redux.Action>,
    _getState: () => Store.State,
  ) => {
    const dispatchThunk = dispatch as any;
    if (orderType === 'collection') {
      dispatchThunk(LoadCollectionDetails(id, false));
    } else {
      dispatchThunk(LoadDeliveryDetails(vrId, id, true));
    }
  };
};
